import {
  ZadCashFlowMethod,
  ZadCustomerAccountRow,
  ZadCustomerAccountStatus,
  ZadManagementReport,
  ZadReportPeriod,
  ZadReportPeriodPreset,
  ZadSupplierAccountStatus,
} from '../../../models/zad'
import {getZadCustomerDisplayName} from './customerHelpers'

export const ZAD_REPORT_PERIOD_PRESETS: ZadReportPeriodPreset[] = [
  'today',
  'last7',
  'thisMonth',
  'lastMonth',
  'thisYear',
  'custom',
]

export const ZAD_REPORT_PERIOD_I18N: Record<ZadReportPeriodPreset, string> = {
  today: 'ZAD.REPORTS.PERIOD_TODAY',
  last7: 'ZAD.REPORTS.PERIOD_LAST7',
  thisMonth: 'ZAD.REPORTS.PERIOD_THIS_MONTH',
  lastMonth: 'ZAD.REPORTS.PERIOD_LAST_MONTH',
  thisYear: 'ZAD.REPORTS.PERIOD_THIS_YEAR',
  custom: 'ZAD.REPORTS.PERIOD_CUSTOM',
}

export const ZAD_CUSTOMER_ACCOUNT_STATUS_I18N: Record<ZadCustomerAccountStatus, string> = {
  RECEIVABLE: 'ZAD.REPORTS.STATUS_RECEIVABLE',
  SETTLED: 'ZAD.REPORTS.STATUS_SETTLED',
  CREDIT: 'ZAD.REPORTS.STATUS_CREDIT',
}

export const ZAD_SUPPLIER_ACCOUNT_STATUS_I18N: Record<ZadSupplierAccountStatus, string> = {
  PAYABLE: 'ZAD.REPORTS.STATUS_PAYABLE',
  SETTLED: 'ZAD.REPORTS.STATUS_SETTLED',
  ADVANCE: 'ZAD.REPORTS.STATUS_ADVANCE',
}

export const ZAD_CASH_FLOW_METHOD_I18N: Record<ZadCashFlowMethod, string> = {
  CASH: 'ZAD.CUSTOMER_PAYMENTS.METHOD_CASH',
  BANK_TRANSFER: 'ZAD.CUSTOMER_PAYMENTS.METHOD_BANK',
  CHEQUE: 'ZAD.CUSTOMER_PAYMENTS.METHOD_CHEQUE',
  CARD: 'ZAD.CUSTOMER_PAYMENTS.METHOD_CARD',
  OTHER: 'ZAD.CUSTOMER_PAYMENTS.METHOD_OTHER',
}

export function toZadIsoDate(value: Date): string {
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(
    value.getDate()
  ).padStart(2, '0')}`
}

export function defaultZadReportPeriod(): {
  startDate: string
  endDate: string
  preset: ZadReportPeriodPreset
} {
  const today = new Date()
  const start = new Date(today.getFullYear(), today.getMonth(), 1)
  return {
    startDate: toZadIsoDate(start),
    endDate: toZadIsoDate(today),
    preset: 'thisMonth',
  }
}

export function periodFromZadPreset(preset: ZadReportPeriodPreset): {
  startDate: string
  endDate: string
} {
  const today = new Date()
  if (preset === 'today') {
    const iso = toZadIsoDate(today)
    return {startDate: iso, endDate: iso}
  }
  if (preset === 'last7') {
    const start = new Date(today)
    start.setDate(today.getDate() - 6)
    return {startDate: toZadIsoDate(start), endDate: toZadIsoDate(today)}
  }
  if (preset === 'thisMonth') {
    return defaultZadReportPeriod()
  }
  if (preset === 'lastMonth') {
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1)
    const end = new Date(today.getFullYear(), today.getMonth(), 0)
    return {startDate: toZadIsoDate(start), endDate: toZadIsoDate(end)}
  }
  if (preset === 'thisYear') {
    const start = new Date(today.getFullYear(), 0, 1)
    return {startDate: toZadIsoDate(start), endDate: toZadIsoDate(today)}
  }
  return defaultZadReportPeriod()
}

export function isZadManagementRole(role?: string | number | null): boolean {
  return String(role) === '2'
}

export function canShowZadManagement(
  role?: string | number | null,
  management?: ZadManagementReport | null
): boolean {
  return isZadManagementRole(role) && Boolean(management)
}

export function formatZadReportCustomerName(
  row?: Pick<ZadCustomerAccountRow, 'displayName' | 'firstName' | 'lastName' | 'customerNumber'> | null
): string {
  if (!row) return ''
  return (
    getZadCustomerDisplayName({
      displayName: row.displayName,
      firstName: row.firstName,
      lastName: row.lastName,
      customerNumber: row.customerNumber,
    }) || row.customerNumber || '—'
  )
}

export interface ZadReportQueryParams {
  startDate?: string
  endDate?: string
  search?: string
  customer?: string
  product?: string
  productType?: string
  category?: string
  supplier?: string
  status?: string
  stockStatus?: string
  lowStockThreshold?: string | number
  isActive?: string
  pageNumber?: number
  pageCount?: number
  sortField?: string
  sortOrder?: string
}

export function buildZadReportQuery(params: ZadReportQueryParams): string {
  const query = new URLSearchParams()
  if (params.startDate) query.set('startDate', params.startDate)
  if (params.endDate) query.set('endDate', params.endDate)
  if (params.search?.trim()) query.set('search', params.search.trim())
  if (params.customer?.trim()) query.set('customer', params.customer.trim())
  if (params.product?.trim()) query.set('product', params.product.trim())
  if (params.productType?.trim()) query.set('productType', params.productType.trim())
  if (params.category?.trim()) query.set('category', params.category.trim())
  if (params.supplier?.trim()) query.set('supplier', params.supplier.trim())
  if (params.status?.trim()) query.set('status', params.status.trim())
  if (params.stockStatus?.trim()) query.set('stockStatus', params.stockStatus.trim())
  if (params.lowStockThreshold !== undefined && params.lowStockThreshold !== '') {
    query.set('lowStockThreshold', String(params.lowStockThreshold))
  }
  if (params.isActive) query.set('isActive', params.isActive)
  if (params.pageNumber && params.pageNumber > 0) query.set('pageNumber', String(params.pageNumber))
  if (params.pageCount && params.pageCount > 0) query.set('pageCount', String(params.pageCount))
  if (params.sortField) query.set('sortField', params.sortField)
  if (params.sortOrder) query.set('sortOrder', params.sortOrder)
  return query.toString()
}

export function formatZadReportPeriodLabel(period?: ZadReportPeriod | null): string {
  if (!period) return ''
  const start = period.startDate ? String(period.startDate).slice(0, 10) : ''
  const end = period.endDate ? String(period.endDate).slice(0, 10) : ''
  if (start && end) return `${start} → ${end}`
  return start || end
}

export function formatZadCustomerAccountSituation(
  outstanding: number
): 'RECEIVABLE' | 'SETTLED' | 'CREDIT' {
  if (outstanding > 0) return 'RECEIVABLE'
  if (outstanding < 0) return 'CREDIT'
  return 'SETTLED'
}

export function formatZadSupplierAccountSituation(
  outstanding: number
): 'PAYABLE' | 'SETTLED' | 'ADVANCE' {
  if (outstanding > 0) return 'PAYABLE'
  if (outstanding < 0) return 'ADVANCE'
  return 'SETTLED'
}
